import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { Check, Users, User, ChevronRight, Gift, Ticket, Trophy, AlertCircle } from "lucide-react";
import axios from "axios";
import SoldOutBanner from "../components/common/SoldOutBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set this to true to show sold out message, false to re-enable registration
const REGISTRATION_CLOSED = true;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    player_type: "individual",
    foursome_members: [
      { name: "", email: "" },
      { name: "", email: "" },
      { name: "", email: "" }
    ],
    dietary_restrictions: "",
    add_ons: {
      fairway_bundle: false,
      fifty_fifty_amount: 0,
      raffle_tickets: 0
    }
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPricing(res.data);
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
      }
    };
    fetchPricing();
  }, []);

  const numGolfers = form.player_type === "foursome" ? 4 : 1;
  const basePrice = pricing?.price || 225;
  const addOnCost = 
    (form.add_ons.fairway_bundle ? 80 * numGolfers : 0) +
    form.add_ons.fifty_fifty_amount +
    (form.add_ons.raffle_tickets * 5);
  const totalPrice = (basePrice * numGolfers) + addOnCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please complete all mandatory fields before submitting.", {
        duration: 5000,
        icon: <AlertCircle className="text-red-500" />
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        player_type: form.player_type,
        foursome_members: form.player_type === "foursome" ? form.foursome_members : null,
        dietary_restrictions: form.dietary_restrictions || null,
        add_ons: {
          fairway_bundle: form.add_ons.fairway_bundle ? numGolfers : 0,
          fifty_fifty_amount: form.add_ons.fifty_fifty_amount,
          raffle_tickets: form.add_ons.raffle_tickets
        }
      };

      const res = await axios.post(`${API}/register`, payload);
      setRegistrationData(res.data);
      setShowConfirmation(true);
      toast.success("Registration successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateFoursomeMember = (index, field, value) => {
    const updated = [...form.foursome_members];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, foursome_members: updated });
  };

  if (pricing?.tier === "closed") {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <AlertCircle size={48} className="mx-auto text-[#C5A059] mb-6" />
          <h1 className="text-4xl mb-4 text-[#1C1C1C]">Registration Closed</h1>
          <p className="text-[#666055] mb-8">
            Unfortunately, registration for the 2026 BISN Charity Golf Classic has closed. 
            Please contact us if you'd like to be added to the waitlist.
          </p>
          <a
            href="mailto:info@bisn.com"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="contact-waitlist-btn"
          >
            Join Waitlist
          </a>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] py-20 px-6">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[#3A5F40] flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl mb-4 text-[#1C1C1C]" data-testid="confirmation-title">
            You're Registered!
          </h1>
          <p className="text-lg text-[#666055] mb-8">
            Thank you for registering, {registrationData?.name}. We can't wait to see you on August 11, 2026!
          </p>

          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 text-left mb-6">
            <h3 className="text-xl mb-4 text-[#1C1C1C]">Registration Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#666055]">Registration Number</span>
                <span className="font-bold text-[#3A5F40] text-lg">#{registrationData?.registration_number || registrationData?.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666055]">Type</span>
                <span className="capitalize text-[#1C1C1C]">{registrationData?.player_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666055]">Total Amount</span>
                <span className="font-medium text-[#3A5F40]">${registrationData?.total_amount?.toLocaleString()} CAD</span>
              </div>
            </div>
          </div>

          {/* Prominent E-Transfer Payment Instructions */}
          <div className="bg-[#3A5F40] rounded-2xl p-6 mb-8 text-white">
            <h3 className="text-xl font-bold mb-3">Complete Your Payment</h3>
            <p className="text-white/90 mb-4">
              Please send your payment of <span className="font-bold">${registrationData?.total_amount?.toLocaleString()} CAD</span> via e-transfer to:
            </p>
            <div className="bg-white/20 rounded-xl p-4 mb-4">
              <p className="text-2xl font-bold">bisngolf@tps.ca</p>
            </div>
            <p className="text-sm text-white/80">
              Include your Registration Number <span className="font-bold">#{registrationData?.registration_number || registrationData?.id}</span> in the e-transfer message.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/")}
              className="btn-secondary"
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
            <Button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: "BISN Charity Golf Classic",
                    text: "I just registered for the BISN Charity Golf Classic supporting SCAO!",
                    url: window.location.origin
                  });
                }
              }}
              className="btn-primary"
              data-testid="share-btn"
            >
              Share with Friends
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F4]">
      {/* Sold Out Banner */}
      <SoldOutBanner />

      <div className="py-12 md:py-20">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
                Registration
              </p>
              <h1 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-4" data-testid="register-title">
                Secure Your Spot
              </h1>
              {!REGISTRATION_CLOSED && pricing && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3A5F40]/10 text-[#3A5F40]">
                  <span className="font-medium">{pricing.label} Rate:</span>
                  <span className="font-bold">${pricing.price}/golfer</span>
                  {pricing.deadline && (
                    <span className="text-sm text-[#666055]">{pricing.deadline}</span>
                  )}
                </div>
              )}
            </div>

            {/* Registration Closed Message */}
            {REGISTRATION_CLOSED && (
              <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8 md:p-12 mb-8 text-center" data-testid="registration-closed">
                <h2 className="text-3xl md:text-4xl font-bold text-[#3A5F40] mb-4">
                  Registration Is Now Closed
                </h2>
                <p className="text-lg text-[#666055]">
                  Thank you for your overwhelming support of the B-ISN Inaugural Charity Golf Classic!
                </p>
              </div>
            )}

            {/* Pricing Comparison - Only show when registration is open */}
            {!REGISTRATION_CLOSED && pricing?.all_tiers && (
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8 mb-8">
              <h2 className="text-2xl mb-4 text-[#1C1C1C]">Registration Rates</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Early Bird */}
                <div className="p-4 rounded-xl border-2 border-[#C5A059] bg-[#C5A059]/5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-[#1C1C1C]">Early Bird</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-[#C5A059] text-white">Current Rate</span>
                  </div>
                  <p className="text-xs text-[#C5A059] font-medium mb-3">Extended for a limited time only!</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#666055]">Individual</span>
                      <span className="font-medium text-[#3A5F40]">$180</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[#666055]">Foursome</span>
                      <span className="font-medium text-[#3A5F40]">$720</span>
                    </div>
                  </div>
                </div>
                
                {/* Regular */}
                <div className="p-4 rounded-xl border-2 border-[#E6E2D6]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-[#1C1C1C]">Regular</h3>
                  </div>
                  <p className="text-xs text-[#666055] mb-3">Standard pricing</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#666055]">Individual</span>
                      <span className="font-medium text-[#3A5F40]">$215</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[#666055]">Foursome</span>
                      <span className="font-medium text-[#3A5F40]">$860</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ticket Purchase Section - Only visible when registration is closed */}
          {REGISTRATION_CLOSED && (
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-2 text-[#1C1C1C]">Purchase Raffle & 50/50 Tickets</h2>
              <p className="text-sm text-[#666055] mb-6">
                Support the cause by purchasing raffle and 50/50 tickets in advance!
              </p>
              
              <div className="space-y-6">
                {/* 50/50 */}
                <div className="p-4 rounded-xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 mb-3">
                    <Ticket size={18} className="text-[#3A5F40]" />
                    <Label className="font-medium text-[#1C1C1C]">50/50 Tickets</Label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[0, 10, 25, 50].map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant={form.add_ons.fifty_fifty_amount === amount ? "default" : "outline"}
                        onClick={() =>
                          setForm({
                            ...form,
                            add_ons: { ...form.add_ons, fifty_fifty_amount: amount }
                          })
                        }
                        className={`rounded-full ${
                          form.add_ons.fifty_fifty_amount === amount
                            ? "bg-[#3A5F40] text-white"
                            : ""
                        }`}
                        data-testid={`fifty-fifty-closed-${amount}`}
                      >
                        {amount === 0 ? "None" : `$${amount}`}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Raffle Tickets */}
                <div className="p-4 rounded-xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy size={18} className="text-[#3A5F40]" />
                    <Label className="font-medium text-[#1C1C1C]">Extra Raffle Tickets</Label>
                    <span className="text-sm text-[#666055]">$5 each</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setForm({
                          ...form,
                          add_ons: {
                            ...form.add_ons,
                            raffle_tickets: Math.max(0, form.add_ons.raffle_tickets - 5)
                          }
                        })
                      }
                      className="rounded-full h-10 w-10"
                      data-testid="raffle-closed-minus"
                    >
                      -
                    </Button>
                    <span className="text-xl font-medium w-16 text-center" data-testid="raffle-closed-count">
                      {form.add_ons.raffle_tickets}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setForm({
                          ...form,
                          add_ons: {
                            ...form.add_ons,
                            raffle_tickets: form.add_ons.raffle_tickets + 5
                          }
                        })
                      }
                      className="rounded-full h-10 w-10"
                      data-testid="raffle-closed-plus"
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>

              {/* Purchase instruction */}
              {(form.add_ons.fifty_fifty_amount > 0 || form.add_ons.raffle_tickets > 0) && (
                <div className="mt-6 p-4 bg-[#3A5F40]/10 border-2 border-[#3A5F40] rounded-xl">
                  <p className="text-base font-bold text-[#3A5F40] text-center mb-2">
                    Ready to Purchase?
                  </p>
                  <p className="text-sm text-[#1C1C1C] text-center leading-relaxed">
                    Total: <span className="font-bold">${(form.add_ons.fifty_fifty_amount + (form.add_ons.raffle_tickets * 5)).toLocaleString()} CAD</span>
                  </p>
                  <p className="text-sm text-[#1C1C1C] text-center leading-relaxed mt-2">
                    Send your payment via e-transfer to{" "}
                    <a href="mailto:bisngolf@tps.ca" className="text-[#3A5F40] font-bold underline">
                      bisngolf@tps.ca
                    </a>
                    {" "}with your name and ticket selections in the memo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Registration Form - Only show when registration is open */}
          {!REGISTRATION_CLOSED && (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Player Type Selection */}
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-6 text-[#1C1C1C]">How are you registering?</h2>
              <RadioGroup
                value={form.player_type}
                onValueChange={(value) => setForm({ ...form, player_type: value })}
                className="grid md:grid-cols-2 gap-4"
              >
                <Label
                  htmlFor="individual"
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    form.player_type === "individual"
                      ? "border-[#3A5F40] bg-[#3A5F40]/5"
                      : "border-[#E6E2D6] hover:border-[#3A5F40]/50"
                  }`}
                  data-testid="individual-option"
                >
                  <RadioGroupItem value="individual" id="individual" />
                  <User size={24} className="text-[#3A5F40]" />
                  <div>
                    <p className="font-medium text-[#1C1C1C]">Individual</p>
                    <p className="text-sm text-[#3A5F40] font-medium">${basePrice} CAD</p>
                  </div>
                </Label>
                <Label
                  htmlFor="foursome"
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    form.player_type === "foursome"
                      ? "border-[#3A5F40] bg-[#3A5F40]/5"
                      : "border-[#E6E2D6] hover:border-[#3A5F40]/50"
                  }`}
                  data-testid="foursome-option"
                >
                  <RadioGroupItem value="foursome" id="foursome" />
                  <Users size={24} className="text-[#3A5F40]" />
                  <div>
                    <p className="font-medium text-[#1C1C1C]">Foursome</p>
                    <p className="text-sm text-[#3A5F40] font-medium">${basePrice * 4} CAD (4 golfers)</p>
                  </div>
                </Label>
              </RadioGroup>
              <p className="text-xs text-[#666055] mt-4 text-center">
                <span className="text-[#C5A059] font-medium">Early Bird Pricing extended for a limited time only!</span> Regular pricing: individual $215 / foursome $860.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-6 text-[#1C1C1C]">Your Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[#666055]">
                    Full Name <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John Smith"
                    className="input-underline"
                    data-testid="input-name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#666055]">
                    Email Address <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="john@example.com"
                    className="input-underline"
                    data-testid="input-email"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-[#666055]">
                    Phone Number <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(416) 555-0123"
                    className="input-underline"
                    data-testid="input-phone"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dietary" className="text-sm font-medium text-[#666055]">
                    Dietary Restrictions (Optional)
                  </Label>
                  <Input
                    id="dietary"
                    value={form.dietary_restrictions}
                    onChange={(e) => setForm({ ...form, dietary_restrictions: e.target.value })}
                    placeholder="e.g., Vegetarian, Gluten-free, Allergies"
                    className="input-underline"
                    data-testid="input-dietary"
                  />
                </div>
              </div>
            </div>

            {/* Foursome Members */}
            {form.player_type === "foursome" && (
              <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
                <h2 className="text-2xl mb-2 text-[#1C1C1C]">Your Foursome</h2>
                <p className="text-sm text-[#666055] mb-6">Add your team members (optional - can be updated later)</p>
                <div className="space-y-6">
                  {form.foursome_members.map((member, index) => (
                    <div key={index} className="grid md:grid-cols-2 gap-4 pb-4 border-b border-[#E6E2D6] last:border-0">
                      <div>
                        <Label className="text-sm font-medium text-[#666055]">
                          Player {index + 2} Name
                        </Label>
                        <Input
                          value={member.name}
                          onChange={(e) => updateFoursomeMember(index, "name", e.target.value)}
                          placeholder="Name"
                          className="input-underline"
                          data-testid={`input-player-${index + 2}-name`}
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#666055]">
                          Player {index + 2} Email
                        </Label>
                        <Input
                          type="email"
                          value={member.email}
                          onChange={(e) => updateFoursomeMember(index, "email", e.target.value)}
                          placeholder="Email"
                          className="input-underline"
                          data-testid={`input-player-${index + 2}-email`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons - Always visible for ticket purchases */}
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-2 text-[#1C1C1C]">
                {REGISTRATION_CLOSED ? "Purchase Raffle & 50/50 Tickets" : "Enhance Your Experience"}
              </h2>
              <p className="text-sm text-[#666055] mb-6">
                {REGISTRATION_CLOSED 
                  ? "Support the cause by purchasing raffle and 50/50 tickets in advance!"
                  : "Optional add-ons to maximize your fun and support"
                }
              </p>
              
              <div className="space-y-6">
                {/* Royal Woodbine Combo - Only show when registration is open */}
                {!REGISTRATION_CLOSED && (
                <div 
                  className={`p-4 rounded-xl border-2 transition-all ${
                    form.add_ons.fairway_bundle
                      ? "border-[#C5A059] bg-[#C5A059]/5"
                      : "border-[#E6E2D6]"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      id="fairway-bundle"
                      checked={form.add_ons.fairway_bundle}
                      onCheckedChange={(checked) =>
                        setForm({
                          ...form,
                          add_ons: { ...form.add_ons, fairway_bundle: checked }
                        })
                      }
                      data-testid="checkbox-fairway"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Gift size={18} className="text-[#C5A059]" />
                        <Label htmlFor="fairway-bundle" className="font-medium text-[#1C1C1C] cursor-pointer">
                          Royal Woodbine Combo
                        </Label>
                        <span className="text-sm font-medium text-[#C5A059]">$80/golfer</span>
                      </div>
                      <p className="text-sm text-[#666055]">
                        Breakfast Combo + 2 mulligans + 8 raffle tickets + 1 contest entry per golfer
                      </p>
                    </div>
                  </div>
                </div>
                )}

                {/* 50/50 */}
                <div className="p-4 rounded-xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 mb-3">
                    <Ticket size={18} className="text-[#3A5F40]" />
                    <Label className="font-medium text-[#1C1C1C]">50/50 Tickets</Label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[0, 10, 25, 50].map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant={form.add_ons.fifty_fifty_amount === amount ? "default" : "outline"}
                        onClick={() =>
                          setForm({
                            ...form,
                            add_ons: { ...form.add_ons, fifty_fifty_amount: amount }
                          })
                        }
                        className={`rounded-full ${
                          form.add_ons.fifty_fifty_amount === amount
                            ? "bg-[#3A5F40] text-white"
                            : ""
                        }`}
                        data-testid={`fifty-fifty-${amount}`}
                      >
                        {amount === 0 ? "None" : `$${amount}`}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Raffle Tickets */}
                <div className="p-4 rounded-xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy size={18} className="text-[#3A5F40]" />
                    <Label className="font-medium text-[#1C1C1C]">Extra Raffle Tickets</Label>
                    <span className="text-sm text-[#666055]">$5 each</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setForm({
                          ...form,
                          add_ons: {
                            ...form.add_ons,
                            raffle_tickets: Math.max(0, form.add_ons.raffle_tickets - 5)
                          }
                        })
                      }
                      className="rounded-full h-10 w-10"
                      data-testid="raffle-minus"
                    >
                      -
                    </Button>
                    <span className="text-xl font-medium w-16 text-center" data-testid="raffle-count">
                      {form.add_ons.raffle_tickets}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setForm({
                          ...form,
                          add_ons: {
                            ...form.add_ons,
                            raffle_tickets: form.add_ons.raffle_tickets + 5
                          }
                        })
                      }
                      className="rounded-full h-10 w-10"
                      data-testid="raffle-plus"
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>

              {/* Purchase instruction for ticket-only buyers */}
              {REGISTRATION_CLOSED && (form.add_ons.fifty_fifty_amount > 0 || form.add_ons.raffle_tickets > 0) && (
                <div className="mt-6 p-4 bg-[#3A5F40]/10 border-2 border-[#3A5F40] rounded-xl">
                  <p className="text-base font-bold text-[#3A5F40] text-center mb-2">
                    Ready to Purchase?
                  </p>
                  <p className="text-sm text-[#1C1C1C] text-center leading-relaxed">
                    Total: <span className="font-bold">${(form.add_ons.fifty_fifty_amount + (form.add_ons.raffle_tickets * 5)).toLocaleString()} CAD</span>
                  </p>
                  <p className="text-sm text-[#1C1C1C] text-center leading-relaxed mt-2">
                    Send your payment via e-transfer to{" "}
                    <a href="mailto:bisngolf@tps.ca" className="text-[#3A5F40] font-bold underline">
                      bisngolf@tps.ca
                    </a>
                    {" "}with your name and ticket selections in the memo.
                  </p>
                </div>
              )}
            </div>

            {/* What's Included */}
            <div className="bg-[#3A5F40]/5 rounded-2xl p-6 md:p-8">
              <h3 className="text-xl mb-4 text-[#1C1C1C]">What's Included</h3>
              <ul className="grid md:grid-cols-2 gap-3">
                {[
                  "18 holes at Royal Woodbine Golf Club",
                  "Golf cart",
                  "Driving range access",
                  "Lunch",
                  "Prizes & awards",
                  "19th Hole Happy Hour"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#666055]">
                    <Check size={16} className="text-[#3A5F40]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-4 text-[#1C1C1C]">Order Summary</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#666055]">
                  <span>Registration ({numGolfers} golfer{numGolfers > 1 ? "s" : ""})</span>
                  <span>${(basePrice * numGolfers).toLocaleString()}</span>
                </div>
                {form.add_ons.fairway_bundle && (
                  <div className="flex justify-between text-[#666055]">
                    <span>Royal Woodbine Combo x{numGolfers}</span>
                    <span>${(80 * numGolfers).toLocaleString()}</span>
                  </div>
                )}
                {form.add_ons.fifty_fifty_amount > 0 && (
                  <div className="flex justify-between text-[#666055]">
                    <span>50/50 Tickets</span>
                    <span>${form.add_ons.fifty_fifty_amount}</span>
                  </div>
                )}
                {form.add_ons.raffle_tickets > 0 && (
                  <div className="flex justify-between text-[#666055]">
                    <span>Raffle Tickets x{form.add_ons.raffle_tickets}</span>
                    <span>${form.add_ons.raffle_tickets * 5}</span>
                  </div>
                )}
                <div className="border-t border-[#E6E2D6] pt-3 flex justify-between">
                  <span className="font-medium text-[#1C1C1C]">Total</span>
                  <span className="text-2xl font-medium text-[#3A5F40]" data-testid="total-price">
                    ${totalPrice.toLocaleString()} CAD
                  </span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="submit-registration"
              >
                {loading ? (
                  "Processing..."
                ) : (
                  <>
                    Complete Registration
                    <ChevronRight size={20} />
                  </>
                )}
              </Button>
              
              {/* E-Transfer Instructions - Prominent */}
              <div className="mt-6 p-4 bg-[#3A5F40]/10 border-2 border-[#3A5F40] rounded-xl">
                <p className="text-base font-bold text-[#3A5F40] text-center mb-2">
                  Payment Instructions
                </p>
                <p className="text-sm text-[#1C1C1C] text-center leading-relaxed">
                  Please complete your registration by sending your payment via e-transfer to{" "}
                  <a href="mailto:bisngolf@tps.ca" className="text-[#3A5F40] font-bold underline">
                    bisngolf@tps.ca
                  </a>
                </p>
                <p className="text-xs text-[#666055] text-center mt-2">
                  Should you require an alternative method of payment, please reach out to us directly at the above address and we will be pleased to accommodate.
                </p>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}
